import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { promoAPI } from '../../api';
import { Spinner, EmptyState, Modal } from '../../components/common/Misc';
import Button from '../../components/common/Button';
import Input  from '../../components/common/Input';
import styles from './AdminDashboard.module.css';

const EMPTY = { code: '', discount_type: 'percent', discount_value: '', min_booking_amount: '', usage_limit: '', expires_at: '' };

const AdminPromos = () => {
  const [promos,  setPromos]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null);
  const [form,    setForm]    = useState(EMPTY);
  const [saving,  setSaving]  = useState(false);

  const fetchPromos = useCallback(async () => {
    setLoading(true);
    try { const res = await promoAPI.getAll(); setPromos(res.data.data); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPromos(); }, [fetchPromos]);

  const openCreate = () => { setForm(EMPTY); setModal('create'); };
  const openEdit   = (p) => { setForm({ ...p, expires_at: p.expires_at ? p.expires_at.slice(0,10) : '' }); setModal(p); };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        discount_value:     Number(form.discount_value),
        min_booking_amount: Number(form.min_booking_amount) || 0,
        usage_limit:        form.usage_limit ? Number(form.usage_limit) : null,
        expires_at:         form.expires_at || null,
        code:               form.code.toUpperCase(),
      };
      if (modal === 'create') await promoAPI.create(payload);
      else                    await promoAPI.update(modal._id, payload);
      toast.success(modal === 'create' ? 'Promo created!' : 'Promo updated!');
      setModal(null); fetchPromos();
    } catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleToggle = async (id) => {
    try { await promoAPI.toggle(id); toast.success('Promo toggled'); fetchPromos(); }
    catch { toast.error('Toggle failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this promo code?')) return;
    try { await promoAPI.delete(id); toast.success('Deleted'); fetchPromos(); }
    catch { toast.error('Delete failed'); }
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Promo codes</h2>
        <Button onClick={openCreate}>+ New promo</Button>
      </div>

      {loading ? <Spinner /> : promos.length === 0
        ? <EmptyState icon="🏷" title="No promo codes" subtitle="Create your first promo" />
        : (
          <div className={styles.tableWrap}>
            <table>
              <thead>
                <tr><th>Code</th><th>Type</th><th>Value</th><th>Min amount</th><th>Usage</th><th>Expires</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {promos.map(p => (
                  <tr key={p._id}>
                    <td><strong style={{ fontFamily:'var(--font-mono)', letterSpacing:'.05em' }}>{p.code}</strong></td>
                    <td style={{ textTransform:'capitalize' }}>{p.discount_type}</td>
                    <td>{p.discount_type === 'percent' ? `${p.discount_value}%` : `₹${p.discount_value}`}</td>
                    <td>₹{p.min_booking_amount}</td>
                    <td>{p.used_count} / {p.usage_limit ?? '∞'}</td>
                    <td>{p.expires_at ? format(new Date(p.expires_at), 'dd MMM yyyy') : ''}</td>
                    <td>
                      <span style={{ color: p.is_active ? 'var(--color-success)' : 'var(--color-text-muted)', fontWeight: 500, fontSize: '.8rem' }}>
                        {p.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className={styles.actionBtns}>
                        <Button size="sm" variant="secondary" onClick={() => openEdit(p)}>Edit</Button>
                        <Button size="sm" variant={p.is_active ? 'danger' : 'success'} onClick={() => handleToggle(p._id)}>
                          {p.is_active ? 'Disable' : 'Enable'}
                        </Button>
                        <Button size="sm" variant="danger" onClick={() => handleDelete(p._id)}>Del</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }

      {modal && (
        <Modal title={modal === 'create' ? 'New promo code' : 'Edit promo'} onClose={() => setModal(null)}>
          <div className={styles.formGrid}>
            <Input label="Code (e.g. SAVE20)" value={form.code} onChange={set('code')} placeholder="WELCOME20" />
            <div className={styles.formRow}>
              <div>
                <label style={{ fontSize:'.85rem', fontWeight:500, color:'var(--color-text-secondary)', display:'block', marginBottom:'.35rem' }}>Discount type</label>
                <select value={form.discount_type} onChange={set('discount_type')} style={{ width:'100%', padding:'.6rem .9rem', border:'1.5px solid var(--color-border)', borderRadius:'var(--radius-md)', fontSize:'.9rem', background:'var(--color-surface)' }}>
                  <option value="percent">Percent (%)</option>
                  <option value="flat">Flat (₹)</option>
                </select>
              </div>
              <Input label="Discount value" type="number" value={form.discount_value} onChange={set('discount_value')} placeholder={form.discount_type === 'percent' ? '20' : '50'} />
            </div>
            <div className={styles.formRow}>
              <Input label="Min booking amount (₹)" type="number" value={form.min_booking_amount} onChange={set('min_booking_amount')} placeholder="0" />
              <Input label="Usage limit (blank = unlimited)" type="number" value={form.usage_limit} onChange={set('usage_limit')} placeholder="100" />
            </div>
            <Input label="Expires on (optional)" type="date" value={form.expires_at} onChange={set('expires_at')} />
            <Button fullWidth loading={saving} onClick={handleSave}>
              {modal === 'create' ? 'Create promo' : 'Save changes'}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AdminPromos;
